// interstate components
import { Box } from '@interstate/components/Box';
import type { ComboBoxOptions } from '@interstate/components/ComboBox';
import { ComboBox } from '@interstate/components/ComboBox';
import { TrashIcon } from '@interstate/components/Icons/TrashIcon';
import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import { NumericInput } from '@interstate/components/NumericInput';

// custom CSS styles
import { discountRowColors, discountRowStyles } from './Discount.style';

// types
import type { DiscountRowProps } from './discountTypes';

// utils
import { extractDiscountOption } from '../utils/discountUtils';

export const DiscountRow: React.FC<DiscountRowProps> = ({
    discount,
    availableOptions,
    selectedValue,
    canRemove,
    onNameChange,
    onAmountChange,
    onRemove,
    onClear,
    nameError,
    amountError
}) => {
    const handleCleanDiscountRow = () => {
        if (canRemove) {
            onRemove();
        } else {
            onClear ? onClear() : (onNameChange(null), onAmountChange(''));
        }
    };

    const handleAmountChange: InterstateOnChangeCallback<string> = (e) => {
        const value = e.target.value;
        onAmountChange(value ?? '');
    };

    const handleComboBoxChange =
        (event: 'inputChange' | 'change' | 'blur') => (e: { target: { value: ComboBoxOptions | string } }) => {
            if (event === 'inputChange' && typeof e.target.value === 'string') {
                const targetValue = e.target.value;
                if (targetValue === '') {
                    onNameChange(null);
                } else {
                    const option = extractDiscountOption(targetValue);
                    if (option) {
                        onNameChange(option);
                    } else {
                        onNameChange(option);
                    }
                }
                return;
            }

            const option = extractDiscountOption(e.target.value);

            if (event === 'blur' && option && !selectedValue?.length) {
                onNameChange(option);
                return;
            }

            if (event === 'blur' && !option) {
                onNameChange(option);
                return;
            }

            if (event === 'change' && option) {
                onNameChange(option);
                return;
            }

            if (option) {
                onNameChange(option);
            }
        };

    return (
        <Box data-testid={`discount-row-${discount.id}`} style={discountRowStyles.container}>
            <Box style={discountRowStyles.dropDownContainer}>
                <ComboBox
                    id={`discount-name-${discount.id}`}
                    data-testid={`discount-name-${discount.id}`}
                    key={`discount-name-${discount.id}`}
                    name={`discount-name-${discount.id}`}
                    label="Name"
                    allowNewEntry={true}
                    hasError={!!nameError}
                    errorMessage={nameError}
                    value={selectedValue}
                    options={availableOptions}
                    placeholder={'Select'}
                    onInputChange={handleComboBoxChange('inputChange')}
                    onBlur={handleComboBoxChange('blur')}
                    onChange={handleComboBoxChange('change')}
                    virtualized={true}
                />
            </Box>

            <Box style={discountRowStyles.numericInputContainer}>
                <NumericInput
                    id={`discount-amount-${discount.id}`}
                    data-testid={`discount-amount-${discount.id}`}
                    key={`discount-amount-${discount.id}`}
                    className={`discount-amount-${discount.id}`}
                    name={`discount-amount-${discount.id}`}
                    inputPrefix="$"
                    placeholder="0.00"
                    label="Amount"
                    value={discount.discountAmount}
                    textAlign="left"
                    onChange={handleAmountChange}
                    allowDecimal={true}
                    decimalMaxLength={2}
                    decimalMinLength={0}
                    autoInsertCommas={true}
                    allowNegative={true}
                    hasError={!!amountError}
                    errorMessage={amountError}
                />
            </Box>

            <Box style={discountRowStyles.removeButtonContainer}>
                <TrashIcon
                    id={`discount-delete-${discount.id}`}
                    data-testid={`discount-delete-${discount.id}`}
                    key={`discount-delete-${discount.id}`}
                    className={`discount-delete-${discount.id}`}
                    name={`discount-delete-${discount.id}`}
                    color={discountRowColors.enabledColor}
                    style={discountRowStyles.trashIcon.enabled}
                    onClick={handleCleanDiscountRow}
                />
            </Box>
        </Box>
    );
};
